import PropTypes from 'prop-types';
import { Tooltip } from '@mui/material';
import { IconSparkles } from '@tabler/icons-react';

import { getScoreColorClassName, getScoreLabel, getScoreTextClassName } from './utils';

// Tooltip content shown on hover: the full critique plus any suggestions.
const FeedbackDetails = ({ critique, suggestions }) => (
  <div className="space-y-2 p-1">
    <div className="flex items-center space-x-1.5">
      <IconSparkles size={14} className="text-primary-300" />
      <span className="text-xs font-semibold uppercase tracking-wide">AI feedback</span>
    </div>

    {critique && <p className="text-sm">{critique}</p>}

    {Array.isArray(suggestions) && suggestions.length > 0 && (
      <ul className="list-disc space-y-1 pl-4">
        {suggestions.map((suggestion, index) => (
          <li key={index} className="text-sm">
            {suggestion}
          </li>
        ))}
      </ul>
    )}
  </div>
);

FeedbackDetails.propTypes = {
  critique: PropTypes.string,
  suggestions: PropTypes.arrayOf(PropTypes.string),
};

FeedbackDetails.defaultProps = {
  critique: undefined,
  suggestions: undefined,
};

// Compact, inline AI feedback indicator intended to sit to the right of an input
// label. Shows the score + label as a pill; hovering opens a tooltip with the
// full critique and suggestions. Renders an "Evaluating" state while loading and
// nothing when there is no feedback.
const ProfileFeedback = ({ feedback, loading }) => {
  if (loading && !feedback) {
    return (
      <span className="flex items-center space-x-1.5 text-sm text-gray-500">
        <IconSparkles size={14} className="animate-pulse text-primary-700" />
        <span>Evaluating&hellip;</span>
      </span>
    );
  }

  if (!feedback) return null;

  const { score, critique, suggestions } = feedback;
  const hasScore = typeof score === 'number';
  const hasDetails = !!critique || (Array.isArray(suggestions) && suggestions.length > 0);

  const pill = (
    <span className="flex cursor-help items-center space-x-1.5">
      {hasScore && <span className={`size-2 rounded-full ${getScoreColorClassName(score)}`} />}
      <span
        className={`whitespace-nowrap text-sm font-semibold ${hasScore ? getScoreTextClassName(score) : 'text-gray-600'}`}
      >
        {hasScore ? `${score}/100 · ${getScoreLabel(score)}` : 'AI feedback'}
      </span>
      {loading === 'regrade' && <span className="text-xs italic text-gray-400">updating&hellip;</span>}
    </span>
  );

  if (!hasDetails) return pill;

  return (
    <Tooltip
      title={<FeedbackDetails critique={critique} suggestions={suggestions} />}
      classes={{
        tooltip: '!rounded-lg !bg-white-100 !text-gray-950 shadow-md border border-gray-50',
      }}
    >
      {pill}
    </Tooltip>
  );
};

ProfileFeedback.propTypes = {
  feedback: PropTypes.shape({
    score: PropTypes.number,
    critique: PropTypes.string,
    suggestions: PropTypes.arrayOf(PropTypes.string),
  }),
  // 'initial' while first generating feedback, 'regrade' while re-evaluating an
  // answer that already has feedback, or falsy when idle.
  loading: PropTypes.oneOf(['initial', 'regrade', false, undefined]),
};

ProfileFeedback.defaultProps = {
  feedback: null,
  loading: false,
};

export default ProfileFeedback;
